import React, { useRef, useEffect, useState } from 'react';
import { ensureGoogleMapsLoaded, getCurrentLocation, reverseGeocode } from '../utils/maps';
import { FaMapMarkerAlt, FaCrosshairs } from 'react-icons/fa';
import { cleanAddress } from '../utils/addressUtils';

const AutocompleteInput = ({ 
  value, 
  onChange, 
  onPlaceSelect, 
  placeholder, 
  name,
  className = '',
  required = false,
  showNearbyOnFocus = true
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const inputIdRef = useRef(`autocomplete-${name || 'input'}-${Math.random().toString(36).substr(2, 9)}`);
  const [isLoaded, setIsLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [showNearbySuggestions, setShowNearbySuggestions] = useState(false);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const [showGoogleSuggestions, setShowGoogleSuggestions] = useState(false);
  const isPlaceSelectedRef = useRef(false);
  const suggestionsRef = useRef(null);
  const timeoutRef = useRef(null);
  const autocompleteServiceRef = useRef(null);

  // Sync input value with prop value (but don't override if user is typing or autocomplete is active)
  useEffect(() => {
    // Only sync if the value changed externally and we haven't just selected a place
    // Also check if input is not focused (to avoid interrupting typing)
    const isInputFocused = inputRef.current === document.activeElement;
    if (!isPlaceSelectedRef.current && value !== inputValue && !isInputFocused) {
      setInputValue(value || '');
    }
    // Reset flag after sync
    if (isPlaceSelectedRef.current) {
      setTimeout(() => {
        isPlaceSelectedRef.current = false;
      }, 100);
    }
  }, [value]);

  // Monitor and ensure pac-container is visible when user types
  useEffect(() => {
    // Don't show suggestions if a place was just selected
    if (isPlaceSelectedRef.current) {
      setShowGoogleSuggestions(false);
      return;
    }
    
    if (!inputValue || inputValue.length === 0) {
      // Hide suggestions when input is empty
      setShowGoogleSuggestions(false);
      return;
    }
    
    // Don't show suggestions if input is not focused (user clicked away)
    if (document.activeElement !== inputRef.current) {
      setShowGoogleSuggestions(false);
      return;
    }
    
    const checkAndShowSuggestions = () => {
      // Don't show if place was just selected
      if (isPlaceSelectedRef.current) {
        return;
      }
      
      const pacContainers = document.querySelectorAll('.pac-container');
      let foundContainer = false;
      
      pacContainers.forEach(container => {
        // Check if this container is related to our input
        const inputRect = inputRef.current?.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // If container is near our input, make sure it's visible
        if (inputRect && containerRect && 
            Math.abs(containerRect.top - (inputRect.bottom + 4)) < 50) {
          container.style.display = 'block';
          container.style.visibility = 'visible';
          container.style.opacity = '1';
          container.style.zIndex = '10002';
          foundContainer = true;
        }
      });
      
      // If no pac-container found but we have input, trigger autocomplete service
      // Only if input is focused and place wasn't just selected
      if (!foundContainer && inputValue.length >= 2 && autocompleteServiceRef.current && 
          document.activeElement === inputRef.current && !isPlaceSelectedRef.current) {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: inputValue,
            types: ['geocode'],
            componentRestrictions: { country: 'in' }
          },
          (predictions, status) => {
            // Double-check place wasn't selected while request was in flight
            if (!isPlaceSelectedRef.current && status === 'OK' && predictions && predictions.length > 0) {
              setGoogleSuggestions(predictions.slice(0, 5));
              setShowGoogleSuggestions(true);
            }
          }
        );
      }
    };

    // Check periodically when user is typing
    const interval = setInterval(checkAndShowSuggestions, 200);
    
    return () => clearInterval(interval);
  }, [inputValue]);

  useEffect(() => {
    let isMounted = true;

    const initializeAutocomplete = async () => {
      try {
        await ensureGoogleMapsLoaded();
        
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.error('Google Places API not loaded');
          if (isMounted) {
            setIsLoaded(true); // Stop spinner even on error
          }
          return;
        }

        // Wait a bit to ensure input is fully rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!inputRef.current) {
          if (isMounted) {
            setIsLoaded(true); // Stop spinner
          }
          return;
        }

        // Set a unique ID if not already set
        if (inputRef.current && !inputRef.current.id) {
          inputRef.current.id = inputIdRef.current;
        }
        
        // Ensure input is visible and accessible
        if (inputRef.current) {
          inputRef.current.style.position = 'relative';
          inputRef.current.style.zIndex = '1';
        }
        
        // Initialize Autocomplete
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['geocode'], // Only geocoding results
          fields: ['geometry', 'formatted_address', 'name', 'place_id'],
          componentRestrictions: { country: 'in' }, // Restrict to India only
        });
        
        // Ensure autocomplete is properly bound
        autocomplete.setFields(['geometry', 'formatted_address', 'name', 'place_id']);
        
        // Debug: Log autocomplete initialization
        console.log('Google Places Autocomplete initialized for:', name || 'input', 'Input ID:', inputRef.current?.id);
        
        // Force autocomplete to show suggestions on focus and input
        if (inputRef.current) {
          const showSuggestions = () => {
            // Ensure pac-container is visible after a short delay
            setTimeout(() => {
              const pacContainers = document.querySelectorAll('.pac-container');
              pacContainers.forEach(container => {
                const inputRect = inputRef.current?.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                // If container is near our input (within 100px vertically), make it visible
                if (inputRect && containerRect && 
                    Math.abs(containerRect.top - (inputRect.bottom + 4)) < 100) {
                  container.style.display = 'block';
                  container.style.visibility = 'visible';
                  container.style.opacity = '1';
                  container.style.zIndex = '10002';
                }
              });
            }, 150);
          };
          
          // Add event listeners to ensure suggestions appear
          inputRef.current.addEventListener('focus', showSuggestions);
          inputRef.current.addEventListener('input', (e) => {
            showSuggestions();
            // Trigger Google's autocomplete by ensuring the value is synced
            if (e.target.value && e.target.value.length >= 2) {
              setTimeout(() => {
                showSuggestions();
              }, 100);
            }
          });
          inputRef.current.addEventListener('keydown', showSuggestions);
        }

        autocompleteRef.current = autocomplete;
        
        // Also initialize AutocompleteService as fallback
        if (window.google && window.google.maps && window.google.maps.places) {
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }

        // Listen for place selection
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (!place.geometry || !place.geometry.location) {
            console.error('Place has no geometry');
            return;
          }

          const location = {
            lat: typeof place.geometry.location.lat === 'function' 
              ? place.geometry.location.lat() 
              : place.geometry.location.lat,
            lng: typeof place.geometry.location.lng === 'function' 
              ? place.geometry.location.lng() 
              : place.geometry.location.lng,
          };

          let address = place.formatted_address || place.name || inputRef.current?.value || '';
          
          // Clean "powered by Google" text from address
          address = cleanAddress(address);

          // Mark that we're selecting a place BEFORE updating state
          isPlaceSelectedRef.current = true;

          // Immediately hide all suggestions when autocomplete place is selected
          setShowNearbySuggestions(false);
          setShowGoogleSuggestions(false);
          
          // Aggressively hide Google's native pac-container immediately
          const hideDropdown = () => {
            const pacContainers = document.querySelectorAll('.pac-container');
            pacContainers.forEach(container => {
              container.style.display = 'none';
              container.style.visibility = 'hidden';
              container.style.opacity = '0';
              container.style.height = '0';
              container.style.width = '0';
              container.style.overflow = 'hidden';
            });
          };
          
          // Hide immediately and also after a short delay to catch any delayed rendering
          hideDropdown();
          setTimeout(hideDropdown, 0);
          setTimeout(hideDropdown, 50);
          setTimeout(hideDropdown, 100);
          setTimeout(hideDropdown, 200);
          
          // Blur the input to ensure dropdown closes
          if (inputRef.current) {
            setTimeout(() => {
              inputRef.current?.blur();
            }, 100);
          }

          // Update local state - use setTimeout to ensure Google's update happens first
          setTimeout(() => {
            setInputValue(address);
            // Update parent component
            if (onChange) {
              onChange({
                target: {
                  name: name,
                  value: address
                }
              });
            }

            if (onPlaceSelect) {
              onPlaceSelect({
                address: address,
                coordinates: location,
                placeId: place.place_id,
                formattedAddress: cleanAddress(place.formatted_address || '')
              });
            }
            
            // Reset flag after a longer delay to prevent suggestions from reappearing
            setTimeout(() => {
              isPlaceSelectedRef.current = false;
            }, 500);
          }, 0);
        });

        if (isMounted) {
          setIsLoaded(true);
        }
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        if (isMounted) {
          setIsLoaded(true); // Stop spinner even on error
        }
      }
    };

    initializeAutocomplete();

    return () => {
      isMounted = false;
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    let newValue = e.target.value;
    // Always update the value - Google autocomplete needs to work with the input
    setInputValue(newValue);
    
    // Ensure autocomplete is still attached and working
    if (autocompleteRef.current && inputRef.current) {
      // Sync the value with the input element (Google autocomplete may have modified it)
      const currentInputValue = inputRef.current.value;
      if (currentInputValue !== newValue) {
        // If Google modified it, use that value instead
        if (currentInputValue !== inputValue) {
          setInputValue(currentInputValue);
          newValue = currentInputValue;
        } else {
          inputRef.current.value = newValue;
        }
      }
      
    }
    
    // Always try to show suggestions when user types (even if autocomplete widget isn't ready)
    // But NOT if a place was just selected
    if (!isPlaceSelectedRef.current && newValue.length >= 2 && document.activeElement === inputRef.current) {
      // Initialize AutocompleteService if not already done
      if (!autocompleteServiceRef.current && window.google && window.google.maps && window.google.maps.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      }
      
      // Use AutocompleteService to show custom suggestions
      if (autocompleteServiceRef.current) {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: newValue,
            types: ['geocode'],
            componentRestrictions: { country: 'in' }
          },
          (predictions, status) => {
            // Double-check place wasn't selected while request was in flight
            if (!isPlaceSelectedRef.current && status === 'OK' && predictions && predictions.length > 0) {
              setGoogleSuggestions(predictions.slice(0, 5));
              setShowGoogleSuggestions(true);
            } else {
              setGoogleSuggestions([]);
              setShowGoogleSuggestions(false);
            }
          }
        );
      }
      
      // Also ensure Google's native pac-container is visible
      // But only if place wasn't just selected
      if (!isPlaceSelectedRef.current) {
        setTimeout(() => {
          // Check again before showing
          if (!isPlaceSelectedRef.current) {
            const pacContainers = document.querySelectorAll('.pac-container');
            pacContainers.forEach(container => {
              if (container) {
                const inputRect = inputRef.current?.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                // Only show if container is near our input
                if (inputRect && containerRect && 
                    Math.abs(containerRect.top - (inputRect.bottom + 4)) < 100) {
                  container.style.display = 'block';
                  container.style.visibility = 'visible';
                  container.style.opacity = '1';
                  container.style.zIndex = '10002';
                }
              }
            });
          }
        }, 50);
      }
    } else if (newValue.length < 2 || isPlaceSelectedRef.current) {
      setGoogleSuggestions([]);
      setShowGoogleSuggestions(false);
    }
    
    if (onChange) {
      onChange(e);
    }
    // Hide nearby suggestions when user starts typing (autocomplete will show instead)
    if (newValue.trim().length > 0) {
      setShowNearbySuggestions(false);
    }
  };

  const loadNearbyPlaces = async () => {
    if (!showNearbyOnFocus || loadingNearby) return;
    
    setLoadingNearby(true);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set a timeout to prevent infinite loading
    timeoutRef.current = setTimeout(() => {
      console.warn('Nearby places loading timeout');
      setLoadingNearby(false);
      timeoutRef.current = null;
    }, 10000); // 10 second timeout
    
    try {
      await ensureGoogleMapsLoaded();
      
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error('Google Maps Places API not available');
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setLoadingNearby(false);
        return;
      }

      // Get current location with high accuracy
      const location = await getCurrentLocation();
      
      // Use PlacesService to get more accurate address details
      await ensureGoogleMapsLoaded();
      const geocoder = new window.google.maps.Geocoder();
      
      const currentLocationData = await new Promise((resolve, reject) => {
        geocoder.geocode(
          { location: { lat: location.lat, lng: location.lng } },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              resolve({
                address: results[0].formatted_address,
                lat: location.lat,
                lng: location.lng,
                placeId: results[0].place_id
              });
            } else {
              // Fallback to reverseGeocode
              reverseGeocode(location.lat, location.lng)
                .then(data => resolve({ ...data, placeId: null }))
                .catch(reject);
            }
          }
        );
      });
      
      setCurrentLocation({
        address: currentLocationData.address,
        coordinates: { lat: currentLocationData.lat, lng: currentLocationData.lng },
        placeId: currentLocationData.placeId
      });

      // Get nearby places using PlacesService
      // PlacesService requires a DOM element - use a hidden div
      const serviceDiv = document.createElement('div');
      serviceDiv.style.display = 'none';
      document.body.appendChild(serviceDiv);
      
      const placesService = new window.google.maps.places.PlacesService(serviceDiv);

      const request = {
        location: new window.google.maps.LatLng(location.lat, location.lng),
        radius: 1000, // 1km radius for more accurate nearby places
        types: ['establishment', 'point_of_interest']
      };

      placesService.nearbySearch(request, (results, status) => {
        console.log('Nearby search status:', status);
        console.log('Nearby search results:', results);
        
        // Clean up the service div
        try {
          if (serviceDiv.parentNode) {
            serviceDiv.parentNode.removeChild(serviceDiv);
          }
        } catch (cleanupError) {
          console.warn('Error cleaning up service div:', cleanupError);
        }
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          try {
            // Calculate distance and sort by proximity
            const currentLatLng = new window.google.maps.LatLng(location.lat, location.lng);
            
            const nearbyPlacesList = results
              .map(place => {
                const placeLocation = place.geometry?.location;
                if (!placeLocation) return null;
                
                const placeLat = typeof placeLocation.lat === 'function' ? placeLocation.lat() : placeLocation.lat;
                const placeLng = typeof placeLocation.lng === 'function' ? placeLocation.lng() : placeLocation.lng;
                const placeLatLng = new window.google.maps.LatLng(placeLat, placeLng);
                
                // Calculate distance in meters
                const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
                  currentLatLng,
                  placeLatLng
                );
                
                return {
                  name: place.name || 'Unknown Place',
                  address: place.vicinity || place.formatted_address || place.name || 'Address not available',
                  coordinates: {
                    lat: placeLat,
                    lng: placeLng
                  },
                  placeId: place.place_id || null,
                  types: place.types || [],
                  distance: distance // Distance in meters
                };
              })
              .filter(place => place !== null && place.distance <= 1000) // Only places within 1km
              .sort((a, b) => a.distance - b.distance) // Sort by distance
              .slice(0, 5); // Get top 5 closest
            
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setNearbyPlaces(nearbyPlacesList);
            setLoadingNearby(false);
          } catch (mapError) {
            console.error('Error processing nearby places:', mapError);
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setLoadingNearby(false);
          }
        } else {
          console.warn('Nearby search status:', status);
          console.warn('Nearby search failed or returned no results. Status:', status);
          
          // Try alternative: use text search for nearby areas
          if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS ||
              status === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED ||
              status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
            console.log('Trying text search as fallback...');
            
            // Create a new service div for text search
            const textServiceDiv = document.createElement('div');
            textServiceDiv.style.display = 'none';
            document.body.appendChild(textServiceDiv);
            
            // Use AutocompleteService with location bias for better nearby results
            const autocompleteService = new window.google.maps.places.AutocompleteService();
            
            // Try multiple queries to get nearby places
            const queries = ['restaurant', 'shop', 'hotel', 'gas station', 'cafe'];
            const allPlaces = [];
            
            const searchPromises = queries.map(query => {
              return new Promise((resolve) => {
                autocompleteService.getPlacePredictions(
                  {
                    input: query,
                    location: new window.google.maps.LatLng(location.lat, location.lng),
                    radius: 1000,
                    types: ['establishment']
                  },
                  (predictions, status) => {
                    if (status === 'OK' && predictions) {
                      resolve(predictions.slice(0, 2)); // Get 2 per query
                    } else {
                      resolve([]);
                    }
                  }
                );
              });
            });
            
            Promise.all(searchPromises).then(resultsArray => {
              const flattened = resultsArray.flat();
              const uniquePlaces = [];
              const seenIds = new Set();
              
              flattened.forEach(prediction => {
                if (!seenIds.has(prediction.place_id)) {
                  seenIds.add(prediction.place_id);
                  uniquePlaces.push({
                    name: prediction.structured_formatting?.main_text || prediction.description,
                    address: prediction.description,
                    placeId: prediction.place_id,
                    coordinates: null, // Will be fetched if selected
                    types: []
                  });
                }
              });
              
              // Get place details for coordinates
              if (uniquePlaces.length > 0) {
                const placesService = new window.google.maps.places.PlacesService(textServiceDiv);
                const placeIds = uniquePlaces.slice(0, 5).map(p => p.placeId);
                
                // Get all place details in parallel
                const detailPromises = placeIds.map((placeId, index) => {
                  return new Promise((resolve) => {
                    placesService.getDetails(
                      { placeId: placeId, fields: ['geometry', 'formatted_address', 'name', 'vicinity'] },
                      (place, placeStatus) => {
                        if (placeStatus === 'OK' && place && place.geometry) {
                          const placeLocation = place.geometry.location;
                          const placeLat = typeof placeLocation.lat === 'function' ? placeLocation.lat() : placeLocation.lat;
                          const placeLng = typeof placeLocation.lng === 'function' ? placeLocation.lng() : placeLocation.lng;
                          
                          uniquePlaces[index].coordinates = { lat: placeLat, lng: placeLng };
                          uniquePlaces[index].address = place.formatted_address || place.vicinity || uniquePlaces[index].address;
                          uniquePlaces[index].name = place.name || uniquePlaces[index].name;
                          
                          // Calculate distance
                          const currentLatLng = new window.google.maps.LatLng(location.lat, location.lng);
                          const placeLatLng = new window.google.maps.LatLng(placeLat, placeLng);
                          uniquePlaces[index].distance = window.google.maps.geometry.spherical.computeDistanceBetween(
                            currentLatLng,
                            placeLatLng
                          );
                        }
                        resolve();
                      }
                    );
                  });
                });
                
                Promise.all(detailPromises).then(() => {
                  // Clean up the service div
                  try {
                    if (textServiceDiv.parentNode) {
                      textServiceDiv.parentNode.removeChild(textServiceDiv);
                    }
                  } catch (cleanupError) {
                    console.warn('Error cleaning up text service div:', cleanupError);
                  }
                  
                  const validPlaces = uniquePlaces
                    .filter(p => p.coordinates !== null && p.distance && p.distance <= 1000)
                    .sort((a, b) => (a.distance || 0) - (b.distance || 0))
                    .slice(0, 5);
                  
                  setNearbyPlaces(validPlaces);
                  
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                  }
                  setLoadingNearby(false);
                });
              } else {
                // Clean up the service div
                try {
                  if (textServiceDiv.parentNode) {
                    textServiceDiv.parentNode.removeChild(textServiceDiv);
                  }
                } catch (cleanupError) {
                  console.warn('Error cleaning up text service div:', cleanupError);
                }
                
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                  timeoutRef.current = null;
                }
                setLoadingNearby(false);
              }
            }).catch(error => {
              console.error('Error in autocomplete fallback:', error);
              // Clean up the service div
              try {
                const textServiceDiv = document.querySelector('div[style*="display: none"]');
                if (textServiceDiv && textServiceDiv.parentNode) {
                  textServiceDiv.parentNode.removeChild(textServiceDiv);
                }
              } catch (cleanupError) {
                console.warn('Error cleaning up text service div:', cleanupError);
              }
              
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setLoadingNearby(false);
            });
          } else {
            // For any other status, just stop loading
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setLoadingNearby(false);
          }
        }
      });
    } catch (error) {
      console.error('Error loading nearby places:', error);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setLoadingNearby(false);
      // Still show current location if we got it
      if (error.message && !error.message.includes('denied')) {
        // If it's not a permission error, try to get current location at least
        try {
          const location = await getCurrentLocation();
          const currentLocationData = await reverseGeocode(location.lat, location.lng);
          setCurrentLocation({
            address: currentLocationData.address,
            coordinates: { lat: currentLocationData.lat, lng: currentLocationData.lng },
            placeId: null
          });
        } catch (e) {
          console.error('Could not get current location:', e);
        }
      }
    }
  };

  const handleFocus = async (e) => {
    // Show nearby suggestions when input is focused and empty
    // Only if user hasn't started typing (autocomplete will handle typing)
    if (showNearbyOnFocus && !inputValue.trim() && !loadingNearby) {
      setShowNearbySuggestions(true);
      loadNearbyPlaces().catch(error => {
        console.error('Error loading nearby places on focus:', error);
      });
    }
    
    // Ensure autocomplete service is initialized
    if (!autocompleteServiceRef.current && window.google && window.google.maps && window.google.maps.places) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
    
    // If there's already text, show suggestions
    if (inputValue && inputValue.length >= 2) {
      if (autocompleteServiceRef.current) {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: inputValue,
            types: ['geocode'],
            componentRestrictions: { country: 'in' }
          },
          (predictions, status) => {
            if (status === 'OK' && predictions && predictions.length > 0) {
              setGoogleSuggestions(predictions.slice(0, 5));
              setShowGoogleSuggestions(true);
            }
          }
        );
      }
    }
  };

  const handleBlur = (e) => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      setShowNearbySuggestions(false);
      setShowGoogleSuggestions(false);
      
      // Also hide Google's native pac-container when input loses focus
      const hideDropdown = () => {
        const pacContainers = document.querySelectorAll('.pac-container');
        pacContainers.forEach(container => {
          container.style.display = 'none';
          container.style.visibility = 'hidden';
          container.style.opacity = '0';
        });
      };
      hideDropdown();
    }, 200);
  };

  const selectSuggestion = (suggestion) => {
    // Clean address before using it
    const cleanedAddress = cleanAddress(suggestion.address);
    
    // Mark that we're selecting a place BEFORE updating state
    isPlaceSelectedRef.current = true;
    
    // Immediately hide all dropdowns
    setInputValue(cleanedAddress);
    setShowNearbySuggestions(false);
    setShowGoogleSuggestions(false);
    
    // Aggressively hide Google's native pac-container
    const hideDropdown = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        container.style.display = 'none';
        container.style.visibility = 'hidden';
        container.style.opacity = '0';
        container.style.height = '0';
        container.style.width = '0';
        container.style.overflow = 'hidden';
      });
    };
    
    // Hide immediately and also after delays to catch any delayed rendering
    hideDropdown();
    setTimeout(hideDropdown, 0);
    setTimeout(hideDropdown, 50);
    setTimeout(hideDropdown, 100);
    setTimeout(hideDropdown, 200);
    
    // Blur the input to close any remaining dropdowns
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current?.blur();
      }, 100);
    }

    if (onChange) {
      onChange({
        target: {
          name: name,
          value: cleanedAddress
        }
      });
    }

    if (onPlaceSelect) {
      onPlaceSelect({
        address: cleanedAddress,
        coordinates: suggestion.coordinates,
        placeId: suggestion.placeId,
        formattedAddress: cleanedAddress
      });
    }
    
    // Reset flag after a longer delay to prevent suggestions from reappearing
    setTimeout(() => {
      isPlaceSelectedRef.current = false;
    }, 500);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside our component
      const isClickOutside = suggestionsRef.current && 
                             !suggestionsRef.current.contains(event.target) && 
                             inputRef.current && 
                             !inputRef.current.contains(event.target);
      
      // Also check if click is not on a pac-container item (Google's native dropdown)
      const isClickOnPacItem = event.target.closest('.pac-item');
      
      if (isClickOutside && !isClickOnPacItem) {
        setShowNearbySuggestions(false);
        setShowGoogleSuggestions(false);
        
        // Also hide Google's native pac-container
        const hideDropdown = () => {
          const pacContainers = document.querySelectorAll('.pac-container');
          pacContainers.forEach(container => {
            container.style.display = 'none';
            container.style.visibility = 'hidden';
            container.style.opacity = '0';
          });
        };
        hideDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative z-10" ref={suggestionsRef}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        className={className}
        autoComplete="off"
        data-autocomplete="true"
        id={inputIdRef.current}
        style={{ 
          // Ensure input is visible and not blocked
          position: 'relative',
          zIndex: 1
        }}
        onKeyDown={(e) => {
          // Ensure autocomplete suggestions appear on keydown
          if (autocompleteRef.current && e.key !== 'Enter' && e.key !== 'Escape') {
            // Sync value immediately for autocomplete
            if (inputRef.current && inputRef.current.value !== inputValue) {
              inputRef.current.value = inputValue;
            }
            
            // Force show suggestions after a short delay
            setTimeout(() => {
              const pacContainers = document.querySelectorAll('.pac-container');
              pacContainers.forEach(container => {
                if (container && inputRef.current?.value && inputRef.current.value.length > 0) {
                  container.style.display = 'block';
                  container.style.visibility = 'visible';
                  container.style.opacity = '1';
                  container.style.zIndex = '10002';
                }
              });
            }, 50);
          }
        }}
      />
      {!isLoaded && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      )}
      
      {/* Google Places Autocomplete Suggestions - Custom dropdown as fallback */}
      {showGoogleSuggestions && googleSuggestions.length > 0 && inputValue.trim().length >= 2 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto" style={{ zIndex: 10001, position: 'absolute', top: '100%' }}>
          {googleSuggestions.map((prediction, index) => (
            <div
              key={prediction.place_id || index}
              className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              onMouseDown={(e) => {
                // Prevent blur event from firing before click completes
                e.preventDefault();
              }}
              onClick={async (e) => {
                // Prevent event bubbling
                e.stopPropagation();
                
                // Mark that we're selecting a place BEFORE updating state
                isPlaceSelectedRef.current = true;
                
                // Immediately hide all dropdowns
                setShowGoogleSuggestions(false);
                setShowNearbySuggestions(false);
                
                // Aggressively hide Google's native pac-container
                const hideDropdown = () => {
                  const pacContainers = document.querySelectorAll('.pac-container');
                  pacContainers.forEach(container => {
                    container.style.display = 'none';
                    container.style.visibility = 'hidden';
                    container.style.opacity = '0';
                    container.style.height = '0';
                    container.style.width = '0';
                    container.style.overflow = 'hidden';
                  });
                };
                
                // Hide immediately and also after delays to catch any delayed rendering
                hideDropdown();
                setTimeout(hideDropdown, 0);
                setTimeout(hideDropdown, 50);
                setTimeout(hideDropdown, 100);
                setTimeout(hideDropdown, 200);
                
                // Get place details
                if (window.google && window.google.maps && window.google.maps.places) {
                  const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
                  placesService.getDetails(
                    { placeId: prediction.place_id, fields: ['geometry', 'formatted_address', 'name'] },
                    (place, status) => {
                      if (status === 'OK' && place && place.geometry) {
                        const location = {
                          lat: typeof place.geometry.location.lat === 'function' 
                            ? place.geometry.location.lat() 
                            : place.geometry.location.lat,
                          lng: typeof place.geometry.location.lng === 'function' 
                            ? place.geometry.location.lng() 
                            : place.geometry.location.lng,
                        };
                        
                        // Clean address before using it
                        const cleanedAddress = cleanAddress(place.formatted_address || prediction.description);
                        
                        setInputValue(cleanedAddress);
                        
                        // Hide dropdowns again after place details are loaded
                        hideDropdown();
                        setTimeout(hideDropdown, 0);
                        setTimeout(hideDropdown, 50);
                        
                        // Blur the input to close any remaining dropdowns
                        if (inputRef.current) {
                          setTimeout(() => {
                            inputRef.current?.blur();
                          }, 100);
                        }
                        
                        if (onChange) {
                          onChange({
                            target: {
                              name: name,
                              value: cleanedAddress
                            }
                          });
                        }
                        
                        if (onPlaceSelect) {
                          onPlaceSelect({
                            address: cleanedAddress,
                            coordinates: location,
                            placeId: prediction.place_id,
                            formattedAddress: cleanAddress(place.formatted_address || '')
                          });
                        }
                        
                        // Reset flag after a longer delay to prevent suggestions from reappearing
                        setTimeout(() => {
                          isPlaceSelectedRef.current = false;
                        }, 500);
                      }
                    }
                  );
                }
              }}
            >
              <div className="flex items-center">
                <FaMapMarkerAlt className="text-blue-600 mr-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{prediction.structured_formatting?.main_text || prediction.description}</p>
                  {prediction.structured_formatting?.secondary_text && (
                    <p className="text-xs text-gray-600 truncate">{prediction.structured_formatting.secondary_text}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Nearby Suggestions Dropdown - Only show when input is empty and focused */}
      {showNearbySuggestions && !inputValue.trim() && (currentLocation || nearbyPlaces.length > 0 || loadingNearby) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto" style={{ zIndex: 10000, position: 'absolute', top: '100%' }}>
          {loadingNearby ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm">Loading nearby places...</p>
            </div>
          ) : (
            <>
              {currentLocation && (
                <div
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-200"
                  onMouseDown={(e) => {
                    // Prevent blur event from firing before click completes
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    // Prevent event bubbling
                    e.stopPropagation();
                    selectSuggestion(currentLocation);
                  }}
                >
                  <div className="flex items-center">
                    <FaCrosshairs className="text-blue-600 mr-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Current Location</p>
                      <p className="text-xs text-gray-600 truncate">{currentLocation.address}</p>
                    </div>
                  </div>
                </div>
              )}
              {nearbyPlaces.length > 0 && (
                <>
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <p className="text-xs font-semibold text-gray-700">Nearby Places</p>
                  </div>
                  {nearbyPlaces.map((place, index) => (
                    <div
                      key={place.placeId || index}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onMouseDown={(e) => {
                        // Prevent blur event from firing before click completes
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        // Prevent event bubbling
                        e.stopPropagation();
                        selectSuggestion(place);
                      }}
                    >
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="text-gray-400 mr-2 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{place.name}</p>
                          <p className="text-xs text-gray-600 truncate">{place.address}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              {!loadingNearby && currentLocation && nearbyPlaces.length === 0 && (
                <div className="p-3 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
                  <p className="text-center">No nearby places found. You can still type to search.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <style>{`
        /* Google Places Autocomplete container - ensure it appears above everything */
        .pac-container {
          z-index: 10002 !important;
          position: absolute !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          margin-top: 4px !important;
          font-family: inherit !important;
          background-color: white !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          max-height: 300px !important;
          overflow-y: auto !important;
          border: 1px solid #e5e7eb !important;
          width: 100% !important;
          left: 0 !important;
          top: 100% !important;
        }
        .pac-item {
          padding: 8px 12px !important;
          cursor: pointer !important;
          border-top: 1px solid #e5e7eb !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
        }
        .pac-item:first-child {
          border-top: none !important;
        }
        .pac-item:hover {
          background-color: #f3f4f6 !important;
        }
        .pac-item-selected {
          background-color: #eff6ff !important;
        }
        .pac-matched {
          font-weight: 500 !important;
        }
        /* Ensure autocomplete works for all inputs */
        input[data-autocomplete="true"] {
          position: relative !important;
          z-index: 1 !important;
        }
        /* Make sure pac-container is visible for all autocomplete inputs */
        #autocomplete-origin ~ .pac-container,
        #autocomplete-destination ~ .pac-container,
        [id^="autocomplete-"] ~ .pac-container,
        .pac-container {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 10002 !important;
          position: absolute !important;
        }
        /* Override any parent container styles that might hide the suggestions */
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default AutocompleteInput;

